import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../../utils/apolloClient';
import config from '../../../utils/config';

/**
 * Augment the privacy protection state block with entries for locations/APIs
 * whose opt-outs were not configured by the user. These will be set to
 * `false`.
 *
 * This returns a modified duplicate of the original state block.
 * It does not modify the original state block in place.
 *
 * @param optOuts {Record<string, Record<string, boolean>>}
 * @returns {Record<string, Record<string, boolean>>}
 */
function padOptOuts({
  entityIdList,
  privacyProtectionSupported,
  privacyProtectionOptOuts
}) {
  const uniqueOrgIds = [...new Set(entityIdList.map(e => e.entityId))];
  const apiOptOutBase = Object.entries(privacyProtectionSupported).reduce(
    (acc, [apiId, supported]) => supported ? { ...acc, [apiId]: false } : acc,
    {}
  );

  const result = uniqueOrgIds.reduce((acc, orgId) => ({
    ...acc,
    [orgId]: apiOptOutBase
  }), {});
  Object.entries(privacyProtectionOptOuts).forEach(([orgId, apiOptOuts]) => {
    result[orgId] = {
      ...result[orgId],
      ...apiOptOuts
    }
  });
  return result;
}

/**
 * Create apiFieldSelections payload using user selected API fields
 * @param selectedApiFields -> an object containing API fields selected by the dealer
 * example - 
 * {
    'api-id-1': {
      apiType: REST/ASYNC,
      fields: ['field-1', 'field-2']
    },
    'api-id-2': {
      apiType: REST/ASYNC,
      fields: ['field-1', 'field-2']
    }
   }
 * @returns apiFieldSelections -> Map of api id to selected fields for that API
   example - 
   {
    'api-id-1': ['field-1', 'field-2'],
    'api-id-2': ['field-1', 'field-2']
   }
 */

const initialState = {
  isSubscriptionComplete: false,
  isSubscriptionIdGenerated: false,
  isConnectionIdGenerated: false,
  isSubscriptionPending: true,
  errorInSubscription: false,
  subscriptionIds: [],
  fetchingSubscriptionId: false,
  pendingAccountVerification:true
};

export const updateCompleteSubscription = createAsyncThunk(
  'updateCompleteSubscription',
  async (
    {
      solutionId,
      entityIdList,
      status,
      token,
      decodedToken,
      planId,
      checkoutDetails,
      address,
      tax,
      checkoutType,
      rShare,
      userComment,
      userEmail,
      appName,
      paymentSetting,
      appSubscriptionActivationType,
      appSubscriptionActivationState,
      locations,
      publisherName,
      firstName,
      publisherEmail,
      emailsForSubscriptionNotifications,
      activateOnly,
      privacyProtectionSupported,
      privacyProtectionOptOuts,
      editFieldSelection,
      dealerApiPreferences,
    },
    thunkAPI
  ) => {
    const cleanOptOuts = padOptOuts({
      entityIdList,
      privacyProtectionSupported,
      privacyProtectionOptOuts,
    });

    const apiFieldsPayload = editFieldSelection;

    let response = await client.authorizeRequest({
      method: 'post',
      url: `${config.serviceEndpoints.mpDpMetadataUrl}/bulkSubscriptions`,
      data: {
        solutionId,
        entityIdList,
        requestedStatus: status,
        selectedPlanId: planId,
        checkoutDetails,
        checkoutType,
        rShare,
        address,
        tax,
        userComment,
        decodedToken,
        userEmail,
        appName,
        paymentSetting,
        appSubscriptionActivationType,
        appSubscriptionActivationState,
        locations,
        publisherName,
        firstName,
        publisherEmail,
        emailsForSubscriptionNotifications,
        activateOnly,
        privacyProtectionOptOuts: cleanOptOuts,
        apiFieldSelections: apiFieldsPayload,
        dealerApiPreferences,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  }
);

export const getSubscriptionId = createAsyncThunk(
  'getSubscriptionId',
  async ({ solutionId, entityId, token }) => {
    let response = await client.authorizeRequest({
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      url: `${config.serviceEndpoints.mpDpMetadataUrl}/consents/entities`,
      data: {
        entityId,
        solutionId,
        consentType: 'subscription'
      }
    });
    return {
      entityId: entityId,
      data:
        response &&
        response.data[0]
    };

  }
);

export const completeSubscriptionSlice = createSlice({
  name: 'reviewSubscriptionSlice',
  initialState,
  reducers: {
    resetSubscriptionFlags: (state) => {
      state.isSubscriptionComplete = false;
      state.isSubscriptionPending = true;
      state.errorInSubscription = false;
      state.subscriptionIds = [];
    }
  },
  extraReducers: {
    [updateCompleteSubscription.fulfilled]: (state,action) => {
      state.isSubscriptionComplete = true;
      state.isSubscriptionPending = false;
      state.errorInSubscription = false;
      state.pendingAccountVerification = action.payload.data[0].pendingAccountVerification;
    },
    [updateCompleteSubscription.pending]: (state) => {
      state.isSubscriptionPending = true;
      state.isSubscriptionComplete = false;
      state.errorInSubscription = false;
    },
    [updateCompleteSubscription.rejected]: (state) => {
      state.errorInSubscription = true;
      state.isSubscriptionComplete = false;
      state.isSubscriptionPending = false;
    },
    [getSubscriptionId.fulfilled]: (state, action) => {
      const data = action.payload.data;
      if (data.subscriptionId && !state.subscriptionIds.includes(data.subscriptionId)) {
        state.subscriptionIds.push(data.subscriptionId)
      }
      state.fetchingSubscriptionId = false;
    },
    [getSubscriptionId.pending]: state => {
      state.fetchingSubscriptionId = true;
    },
    [getSubscriptionId.rejected]: state => {
      state.fetchingSubscriptionId = false;
    },
  }
});

export const { resetSubscriptionFlags } = completeSubscriptionSlice.actions;

export default completeSubscriptionSlice.reducer;
